// Drug-drug interaction (DDI) matrix — curated list of clinically-important
// interactions relevant to Indian outpatient practice. Each entry declares a
// PAIR of molecule patterns and returns a severity + mechanism + management
// tip. Order-independent (A↔B matches regardless of prescription order).
//
// Severity levels:
//   critical → contraindicated / avoid unless specialist supervision
//   major    → potentially serious; requires dose adjustment or monitoring
//   moderate → clinically significant; monitor
//   minor    → theoretical or mild; usually manageable

const rxHas = (medicines, needle) =>
  (medicines || []).some((m) => `${m.generic_name || ""} ${m.composition || ""} ${m.brand_name || ""}`.toLowerCase().includes(needle));

const rxHasAny = (medicines, arr) => arr.some((needle) => rxHas(medicines, needle));

// The primary rule table. `left` and `right` are arrays of case-insensitive
// substrings that identify each drug class / molecule.
export const DDI_RULES = [
  // ==== Bleeding / anticoagulant interactions ====
  {
    key: "warfarin+nsaid",
    left: ["warfarin"], right: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "etoricoxib"],
    severity: "critical",
    label: "Warfarin + NSAID → serious GI/intracranial bleeding",
    mechanism: "NSAIDs displace warfarin from albumin, inhibit platelet aggregation, and cause gastric mucosal injury — multiple mechanisms all pushing bleeding risk up ~4×.",
    management: "AVOID this combination. Use paracetamol for analgesia. If NSAID truly essential, use lowest dose for shortest duration + PPI cover + weekly INR + written bleeding advice.",
  },
  {
    key: "warfarin+aspirin",
    left: ["warfarin"], right: ["aspirin", "ecosprin"],
    severity: "major",
    label: "Warfarin + Aspirin → bleeding risk",
    mechanism: "Additive anticoagulant + antiplatelet effects. Doubles major bleeding risk.",
    management: "Only combine if strong indication (mechanical valve, recent PCI). Add PPI. Target lower INR (2.0-2.5).",
  },
  {
    key: "warfarin+amiodarone",
    left: ["warfarin"], right: ["amiodarone"],
    severity: "major",
    label: "Warfarin + Amiodarone → CYP2C9 inhibition ↑ INR",
    mechanism: "Amiodarone inhibits CYP2C9/CYP3A4 → warfarin levels rise over 1-3 weeks.",
    management: "Reduce warfarin dose by 25-50 % preemptively. Recheck INR weekly for first month.",
  },
  {
    key: "doac+nsaid",
    left: ["apixaban", "rivaroxaban", "dabigatran", "edoxaban"], right: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac"],
    severity: "major",
    label: "DOAC + NSAID → bleeding risk",
    mechanism: "Combined antiplatelet + anticoagulant effect on gastric mucosa.",
    management: "Prefer paracetamol / short-course opioids. If NSAID essential, add PPI + brief course only.",
  },
  {
    key: "doac+azole",
    left: ["apixaban", "rivaroxaban"], right: ["ketoconazole", "itraconazole", "voriconazole"],
    severity: "critical",
    label: "Apixaban / Rivaroxaban + strong CYP3A4 inhibitor → toxicity",
    mechanism: "CYP3A4 + P-gp inhibition raises DOAC levels 2-4×.",
    management: "AVOID. Use fluconazole (weak inhibitor) instead, or switch to LMWH for the antifungal course.",
  },

  // ==== Statin interactions ====
  {
    key: "statin+macrolide",
    left: ["simvastatin", "atorvastatin"], right: ["clarithromycin", "erythromycin"],
    severity: "major",
    label: "Statin (Simva/Atorva) + Macrolide → rhabdomyolysis risk",
    mechanism: "Macrolides inhibit CYP3A4 → statin AUC rises 3-10×.",
    management: "HOLD statin for the macrolide course (5-7 days). Alternatively use Azithromycin (weak CYP3A4 inhibitor), or switch to Rosuvastatin/Pravastatin (not CYP3A4 metabolised).",
  },
  {
    key: "statin+fibrate",
    left: ["simvastatin", "atorvastatin", "rosuvastatin", "pravastatin"], right: ["gemfibrozil"],
    severity: "critical",
    label: "Statin + Gemfibrozil → rhabdomyolysis",
    mechanism: "Gemfibrozil inhibits statin glucuronidation and OATP1B1 → statin levels rise 3-6×.",
    management: "AVOID. Use Fenofibrate (much lower risk) if a statin+fibrate combo is genuinely needed. Monitor CK if suspected muscle pain.",
  },
  {
    key: "statin+colchicine",
    left: ["simvastatin", "atorvastatin", "rosuvastatin"], right: ["colchicine"],
    severity: "moderate",
    label: "Statin + Colchicine → myopathy risk",
    mechanism: "Both cause myopathy; combined risk higher, especially in CKD/elderly.",
    management: "Use lowest effective statin dose. Monitor for muscle pain; check CK if symptomatic. Reduce colchicine dose in CKD.",
  },

  // ==== RAAS & Potassium ====
  {
    key: "raas+kspare",
    left: ["ramipril", "enalapril", "lisinopril", "perindopril", "telmisartan", "losartan", "olmesartan", "valsartan", "irbesartan", "candesartan", "sacubitril"],
    right: ["spironolactone", "eplerenone", "amiloride", "triamterene", "finerenone"],
    severity: "major",
    label: "RAASi + K-sparing diuretic → hyperkalaemia",
    mechanism: "Both reduce potassium excretion → cumulative hyperkalaemia (esp in CKD).",
    management: "Combination is a HFrEF cornerstone but requires K + creat monitoring at 1, 4, 12 weeks and any dose change. Hold if K > 5.5.",
  },
  {
    key: "raas+kcl",
    left: ["ramipril", "enalapril", "telmisartan", "losartan", "valsartan", "olmesartan", "sacubitril", "spironolactone", "eplerenone"],
    right: ["potassium chloride", "kcl supplement", "k-supp"],
    severity: "major",
    label: "RAASi/MRA + Potassium supplement → hyperkalaemia",
    mechanism: "Additive potassium retention.",
    management: "Rarely both needed. Deprescribe K supplement unless dietary K deficit documented. Monitor K weekly.",
  },
  {
    key: "raas+trimethoprim",
    left: ["ramipril", "enalapril", "telmisartan", "losartan", "valsartan", "sacubitril"], right: ["trimethoprim", "cotrimoxazole"],
    severity: "major",
    label: "RAASi + Trimethoprim → hyperkalaemia",
    mechanism: "Trimethoprim blocks ENaC — acts like a K-sparing agent — plus reduces creatinine clearance.",
    management: "Avoid cotrimoxazole in elderly on RAASi. Use alternative antibiotic where possible. Check K on day 3 of course.",
  },

  // ==== Diabetes drugs ====
  {
    key: "insulin+su+bb",
    left: ["insulin", "glimepiride", "gliclazide"], right: ["propranolol", "atenolol"],
    severity: "moderate",
    label: "Hypoglycaemic + Non-selective β-blocker → masked hypoglycaemia",
    mechanism: "Non-selective β-blockers blunt adrenergic warning signs (tremor, palpitations) and delay glucose recovery.",
    management: "Prefer cardioselective β-blocker (Bisoprolol / Metoprolol / Nebivolol). Educate patient — hunger and sweating remain.",
  },
  {
    key: "sglt2+loop",
    left: ["empagliflozin", "dapagliflozin", "canagliflozin"], right: ["furosemide", "torsemide"],
    severity: "moderate",
    label: "SGLT2i + Loop diuretic → volume depletion",
    mechanism: "Additive natriuresis and osmotic diuresis.",
    management: "Reduce loop diuretic dose by ~25 % when starting SGLT2i in a euvolaemic HF patient. Monitor BP, weight, creatinine at 2 weeks.",
  },
  {
    key: "metformin+contrast",
    left: ["metformin"], right: ["iohexol contrast", "iodixanol contrast", "iopamidol contrast", "iomeprol contrast"],
    severity: "moderate",
    label: "Metformin + IV Iodinated contrast → lactic acidosis risk",
    mechanism: "Contrast-induced AKI + retained metformin.",
    management: "Hold metformin from contrast day and for 48 hours after. Resume once creatinine confirmed stable. Not needed if eGFR ≥ 60 and small contrast volume.",
  },
  {
    key: "sglt2+diuretic-dka",
    left: ["empagliflozin", "dapagliflozin", "canagliflozin"], right: ["insulin"],
    severity: "moderate",
    label: "SGLT2i + Insulin dose reduction → euglycaemic DKA risk",
    mechanism: "Over-aggressive insulin reduction on SGLT2i can precipitate euglycaemic DKA (blood sugar looks normal, ketones high).",
    management: "Reduce insulin by 10-20 % only. Educate on sick-day rules and euglycaemic DKA symptoms (nausea, vomiting, breathlessness).",
  },

  // ==== Cardiac ====
  {
    key: "ccb-nondhp+bb",
    left: ["verapamil", "diltiazem"], right: ["bisoprolol", "carvedilol", "metoprolol", "atenolol", "propranolol", "nebivolol"],
    severity: "critical",
    label: "Verapamil/Diltiazem + β-blocker → severe bradycardia / AV block",
    mechanism: "Additive AV nodal blockade + negative inotropy.",
    management: "AVOID unless supervised (e.g. inpatient HOCM). If genuinely required, ECG monitoring, start ultra-low dose.",
  },
  {
    key: "digoxin+amiodarone",
    left: ["digoxin"], right: ["amiodarone"],
    severity: "major",
    label: "Digoxin + Amiodarone → digoxin toxicity",
    mechanism: "Amiodarone inhibits P-gp → digoxin levels rise ~70 %.",
    management: "Reduce digoxin dose by 50 % on starting amiodarone. Check digoxin level in 5-7 days.",
  },
  {
    key: "digoxin+diuretic",
    left: ["digoxin"], right: ["furosemide", "torsemide", "hydrochlorothiazide", "chlorthalidone", "indapamide"],
    severity: "moderate",
    label: "Digoxin + Diuretic → hypokalaemia potentiates digoxin toxicity",
    mechanism: "Diuretic-induced K depletion sensitises myocardium to digoxin.",
    management: "Keep K > 4.0. Consider K-sparing addition (spironolactone). Watch for nausea, visual halos, arrhythmias.",
  },
  {
    key: "amiodarone+qtc",
    left: ["amiodarone"], right: ["azithromycin", "clarithromycin", "domperidone", "ondansetron", "haloperidol"],
    severity: "major",
    label: "Amiodarone + QT-prolonging drug → Torsades de Pointes risk",
    mechanism: "Additive QT prolongation.",
    management: "Avoid combination. If unavoidable, baseline + on-therapy ECG, correct K/Mg, use for shortest duration.",
  },

  // ==== Others ====
  {
    key: "clopidogrel+ppi",
    left: ["clopidogrel"], right: ["omeprazole", "esomeprazole"],
    severity: "moderate",
    label: "Clopidogrel + Omeprazole/Esomeprazole → reduced clopidogrel activation",
    mechanism: "Omeprazole/Esomeprazole are strong CYP2C19 inhibitors — clopidogrel is a prodrug activated by CYP2C19.",
    management: "Switch PPI to Pantoprazole or Rabeprazole (minimal CYP2C19 effect). Documented outcome differences are modest but the safe swap is easy.",
  },
  {
    key: "levothyroxine+ca-fe-ppi",
    left: ["levothyroxine"], right: ["calcium carbonate", "ferrous sulphate", "ferrous ascorbate", "aluminium hydroxide", "sucralfate"],
    severity: "moderate",
    label: "Levothyroxine + Calcium/Iron/Antacid → reduced absorption",
    mechanism: "Chelation in gut prevents thyroxine absorption (up to 50 % reduction).",
    management: "Take levothyroxine at least 4 hours apart from these agents. Preferably levothyroxine 30 min before breakfast; calcium/iron at lunch or dinner.",
  },
  {
    key: "levothyroxine+ppi",
    left: ["levothyroxine"], right: ["pantoprazole", "omeprazole", "rabeprazole", "esomeprazole"],
    severity: "minor",
    label: "Levothyroxine + PPI → reduced absorption",
    mechanism: "Gastric acid needed to release/absorb thyroxine; PPI reduces absorption ~25 %.",
    management: "Space by ≥ 4 hours. Recheck TSH 6 weeks after starting/stopping PPI — dose may need adjustment.",
  },
  {
    key: "ssri+nsaid",
    left: ["fluoxetine", "sertraline", "escitalopram", "paroxetine"], right: ["ibuprofen", "diclofenac", "naproxen", "aspirin"],
    severity: "moderate",
    label: "SSRI + NSAID/Aspirin → upper GI bleeding",
    mechanism: "SSRIs deplete platelet serotonin — combined effect with NSAID is 3-4× baseline GI bleed risk.",
    management: "Add PPI cover. Consider alternative analgesic (paracetamol).",
  },
  {
    key: "allopurinol+azathioprine",
    left: ["allopurinol"], right: ["azathioprine", "mercaptopurine"],
    severity: "critical",
    label: "Allopurinol + Azathioprine → life-threatening myelosuppression",
    mechanism: "Allopurinol inhibits xanthine oxidase which normally metabolises azathioprine.",
    management: "AVOID combination. Use febuxostat instead of allopurinol, or reduce azathioprine dose to 25 % of usual + weekly CBC.",
  },
  {
    key: "tramadol+ssri",
    left: ["tramadol"], right: ["fluoxetine", "sertraline", "escitalopram", "paroxetine", "duloxetine"],
    severity: "major",
    label: "Tramadol + SSRI → Serotonin Syndrome + seizure",
    mechanism: "Both are serotonergic; tramadol also lowers seizure threshold.",
    management: "Avoid. Use paracetamol / short-course opioids without serotonergic activity.",
  },
  {
    key: "sildenafil+nitrate",
    left: ["sildenafil", "tadalafil", "vardenafil"], right: ["nitroglycerine", "isosorbide"],
    severity: "critical",
    label: "PDE5 inhibitor + Nitrate → catastrophic hypotension",
    mechanism: "Both cause vasodilation via NO/cGMP; additive drop in BP can be fatal.",
    management: "ABSOLUTE contraindication. Advise patients on nitrates never to take PDE5 inhibitors. Wait ≥ 24 h (sildenafil) or 48 h (tadalafil) after last PDE5 dose before giving nitrate for angina.",
  },
  {
    key: "carbamazepine+ocp",
    left: ["carbamazepin", "phenytoin", "rifampicin"], right: ["oral contraceptive", "ethinylestradiol", "levonorgestrel", "drospirenone"],
    severity: "major",
    label: "Enzyme inducer + Oral contraceptive → contraceptive failure",
    mechanism: "CYP3A4 induction reduces OCP hormone levels.",
    management: "Use non-hormonal contraception (IUD, condoms) or long-acting depot MPA. Do NOT rely on standard OCP.",
  },
  {
    key: "colchicine+clarithro",
    left: ["colchicine"], right: ["clarithromycin", "erythromycin"],
    severity: "critical",
    label: "Colchicine + Clarithromycin → fatal colchicine toxicity",
    mechanism: "Macrolide inhibits CYP3A4 and P-gp — colchicine levels rise dramatically.",
    management: "AVOID. Especially dangerous in CKD/hepatic impairment. Use azithromycin instead.",
  },

  // ==== Steroid family (previously absent) ====
  {
    key: "steroid+nsaid",
    left: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone", "betamethasone", "deflazacort"],
    right: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "etoricoxib", "indomethacin"],
    severity: "critical",
    label: "Corticosteroid + NSAID → severe GI bleed",
    mechanism: "Additive gastric mucosal injury — combined risk of major GI bleed rises 4-15×.",
    management: "AVOID. Prefer paracetamol / short-course opioid. If both essential, add PPI (Pantoprazole 40 mg OD) + counsel on GI-bleed warning signs.",
  },
  {
    key: "steroid+anticoag",
    left: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone"],
    right: ["warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban"],
    severity: "major",
    label: "Corticosteroid + Anticoagulant → bleeding risk",
    mechanism: "Steroids raise GI-bleed baseline; anticoagulation amplifies. Warfarin: unpredictable INR shifts.",
    management: "Add PPI cover. Monitor INR twice-weekly for warfarin. Watch for melaena/haematuria.",
  },
  {
    key: "steroid+diabetes",
    left: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone", "deflazacort"],
    right: ["metformin", "glimepiride", "gliclazide", "insulin", "empagliflozin", "dapagliflozin", "sitagliptin", "linagliptin", "vildagliptin", "semaglutide", "dulaglutide"],
    severity: "major",
    label: "Corticosteroid + Diabetes med → steroid-induced hyperglycaemia",
    mechanism: "Glucocorticoids raise post-prandial glucose (especially at lunch/dinner if morning steroid) via ↑hepatic glucose output and insulin resistance.",
    management: "Anticipate 20-40 % rise in insulin/SU requirement. Start home glucose monitoring 4×/day during the steroid course. Wean insulin as steroid tapers.",
  },
  {
    key: "steroid+furosemide",
    left: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone"],
    right: ["furosemide", "torsemide", "bumetanide"],
    severity: "moderate",
    label: "Corticosteroid + Loop diuretic → hypokalaemia",
    mechanism: "Both cause K+ loss (steroid mineralocorticoid effect + loop kaliuresis).",
    management: "Recheck K+ at 3-7 days. Consider adding K-sparing MRA if K trending down; supplement K if < 3.5.",
  },
  {
    key: "steroid+digoxin",
    left: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone"],
    right: ["digoxin"],
    severity: "moderate",
    label: "Corticosteroid + Digoxin → toxicity via hypokalaemia",
    mechanism: "Steroid-induced hypoK sensitises myocardium to digoxin at any level.",
    management: "Keep K > 4.0. Consider halving digoxin dose during steroid pulses. Watch for nausea, visual halos, bradyarrhythmia.",
  },

  // ==== Anti-infective additions ====
  {
    key: "aminoglycoside+loop",
    left: ["gentamicin", "amikacin", "tobramycin", "streptomycin", "netilmicin"],
    right: ["furosemide", "torsemide", "bumetanide"],
    severity: "critical",
    label: "Aminoglycoside + Loop diuretic → oto/nephrotoxicity",
    mechanism: "Both are ototoxic (cochlear hair-cell damage) and nephrotoxic; combined risk 2-3×.",
    management: "Avoid if possible. If unavoidable: single-daily-dose aminoglycoside, monitor troughs, baseline + weekly audiogram in prolonged courses, daily creatinine.",
  },
  {
    key: "fq+steroid",
    left: ["ciprofloxacin", "levofloxacin", "moxifloxacin", "ofloxacin", "norfloxacin"],
    right: ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone"],
    severity: "major",
    label: "Fluoroquinolone + Corticosteroid → tendon rupture",
    mechanism: "FQ-induced tendinopathy is amplified by steroids (Achilles > 90 % of ruptures). Risk highest in age ≥ 60, CKD, transplant.",
    management: "AVOID this combination especially in elderly/renal impaired/transplant. Use alternative antibiotic. Educate patient — stop drug + rest at any tendon pain.",
  },
  {
    key: "fq+sulfonylurea",
    left: ["ciprofloxacin", "levofloxacin", "moxifloxacin", "gatifloxacin"],
    right: ["glimepiride", "gliclazide", "glipizide", "glibenclamide"],
    severity: "major",
    label: "Fluoroquinolone + Sulfonylurea → dysglycaemia",
    mechanism: "FQ (esp. gatifloxacin, levofloxacin, ciprofloxacin) cause both hypo- AND hyper-glycaemia via β-cell effects.",
    management: "Monitor CBG q6h during FQ course. Consider alternative antibiotic in elderly/CKD. Educate patient on hypoglycaemia symptoms.",
  },
  {
    key: "fq+qt-drug",
    left: ["ciprofloxacin", "levofloxacin", "moxifloxacin", "gatifloxacin"],
    right: ["amiodarone", "sotalol", "haloperidol", "citalopram", "escitalopram", "domperidone", "ondansetron"],
    severity: "critical",
    label: "Fluoroquinolone + QT-prolonging drug → Torsades",
    mechanism: "Additive QT prolongation. Moxifloxacin has the highest signal; levofloxacin next.",
    management: "AVOID moxifloxacin. Use amoxicillin-clavulanate or doxycycline where possible. ECG + K/Mg correction if unavoidable.",
  },
  {
    key: "warfarin+antimicrobial",
    left: ["warfarin", "acenocoumarol"],
    right: ["clarithromycin", "erythromycin", "azithromycin", "ketoconazole", "fluconazole", "itraconazole", "metronidazole", "doxycycline", "cotrimoxazole", "trimethoprim"],
    severity: "major",
    label: "Warfarin + Antimicrobial → ↑INR",
    mechanism: "CYP2C9/CYP3A4 inhibition + gut-flora Vit K disruption raises INR unpredictably.",
    management: "Recheck INR at day 3-5. Anticipate warfarin dose reduction 20-30 % during course. Counsel on bleeding signs.",
  },
  {
    key: "rifampin+cyp-substrates",
    left: ["rifampin", "rifampicin"],
    right: ["warfarin", "apixaban", "rivaroxaban", "amlodipine", "verapamil", "diltiazem", "simvastatin", "atorvastatin", "glipizide", "glimepiride", "cyclosporine", "tacrolimus", "prednisolone", "phenytoin", "carbamazepin", "ethinylestradiol", "sacubitril"],
    severity: "critical",
    label: "Rifampin + CYP450 substrate → severe efficacy loss",
    mechanism: "Rifampin is a potent CYP3A4/2C9/P-gp inducer — reduces substrate AUC 30-90 %.",
    management: "AVOID. If TB treatment mandatory, switch substrate to non-CYP-metabolised alternative (e.g. Pravastatin, LMWH) for the duration + 2 weeks post-course.",
  },
  {
    key: "linezolid+ssri",
    left: ["linezolid"],
    right: ["sertraline", "fluoxetine", "escitalopram", "paroxetine", "citalopram", "venlafaxine", "duloxetine", "mirtazapine"],
    severity: "critical",
    label: "Linezolid + SSRI/SNRI → Serotonin Syndrome",
    mechanism: "Linezolid is a reversible MAOI — combined with serotonergic agent → agitation, hyperthermia, clonus, autonomic instability.",
    management: "AVOID. If linezolid essential, stop SSRI/SNRI ≥ 2 weeks prior (5 weeks for fluoxetine). Restart antidepressant 24 h after last linezolid dose.",
  },
  {
    key: "metronidazole+alcohol",
    guard: (flags, meds) => flags?.alcoholMisuse || rxHasAny(meds || [], ["metronidazole"]),
    left: ["metronidazole", "tinidazole"],
    right: ["alcohol", "ethanol"],
    severity: "critical",
    label: "Metronidazole + Alcohol → disulfiram-like reaction",
    mechanism: "Metronidazole inhibits aldehyde dehydrogenase → acetaldehyde accumulation causes flushing, vomiting, tachycardia, hypotension.",
    management: "Counsel patient: NO alcohol during course + 3 days after. Also check alcohol-containing preparations (cough syrups, mouthwash).",
  },
  {
    key: "digoxin+macrolide",
    left: ["digoxin"], right: ["clarithromycin", "erythromycin", "azithromycin"],
    severity: "major",
    label: "Digoxin + Macrolide → ↑digoxin levels",
    mechanism: "Macrolides inhibit intestinal P-gp → digoxin absorption ↑ up to 2×.",
    management: "Halve digoxin dose during macrolide course + monitor for toxicity (nausea, halos, AV block).",
  },

  // ==== Diabetes-family additions ====
  {
    key: "insulin+pioglitazone",
    left: ["insulin", "glargine", "degludec", "detemir", "lispro", "aspart"],
    right: ["pioglitazone", "rosiglitazone"],
    severity: "major",
    label: "Insulin + Pioglitazone → fluid retention / HF",
    mechanism: "Both promote sodium/water retention. Combined risk of HF hospitalisation 2-3× vs insulin alone.",
    management: "Avoid in NYHA III-IV HF. Weigh weekly; report ankle oedema or breathlessness immediately. Consider metformin + SGLT2 instead.",
  },
  {
    key: "glp1+su-insulin",
    left: ["semaglutide", "liraglutide", "dulaglutide", "tirzepatide", "exenatide"],
    right: ["glimepiride", "gliclazide", "glipizide", "glibenclamide", "insulin"],
    severity: "moderate",
    label: "GLP-1 agonist + Sulfonylurea/Insulin → hypoglycaemia",
    mechanism: "Additive glucose-lowering. GLP-1 alone rarely causes hypo, but with SU/insulin the risk is significant.",
    management: "Reduce SU dose by 50 % OR reduce basal insulin by 20 % when starting GLP-1. Educate on hypoglycaemia symptoms.",
  },
  {
    key: "insulin+alcohol",
    guard: (flags) => !!flags?.alcoholMisuse,
    left: ["insulin", "glargine", "degludec", "detemir"],
    right: ["insulin", "glargine", "degludec", "detemir"], // matches when any insulin present
    severity: "critical",
    label: "Insulin + Alcohol misuse → severe delayed hypoglycaemia",
    mechanism: "Alcohol inhibits hepatic gluconeogenesis → prolonged hypoglycaemia 6-24 h after drinking, often overnight.",
    management: "Counsel patient: eat carbs when drinking, never drink on empty stomach, keep glucose gel at bedside, consider CGM. Reduce basal insulin on drinking nights.",
  },

  // ==== Cardio / CNS additions ====
  {
    key: "amiodarone+bb",
    left: ["amiodarone"], right: ["bisoprolol", "carvedilol", "metoprolol", "atenolol", "propranolol", "nebivolol"],
    severity: "moderate",
    label: "Amiodarone + β-blocker → bradycardia / AV block",
    mechanism: "Additive negative chronotropy + AV nodal blockade.",
    management: "Monitor HR + ECG. Halve BB starting dose. Pacemaker fallback in HFrEF patients on both.",
  },
  {
    key: "triptan+ssri",
    left: ["sumatriptan", "rizatriptan", "zolmitriptan", "eletriptan", "almotriptan", "naratriptan"],
    right: ["sertraline", "fluoxetine", "escitalopram", "paroxetine", "citalopram", "venlafaxine", "duloxetine"],
    severity: "moderate",
    label: "Triptan + SSRI/SNRI → Serotonin Syndrome (rare)",
    mechanism: "Both serotonergic; FDA warning 2006 but real-world incidence low.",
    management: "Educate patient on serotonin syndrome symptoms (agitation, hyperreflexia, hyperthermia). Use lowest triptan dose. Avoid combining multiple serotonergic agents.",
  },
  {
    key: "lithium+nephrotoxic",
    left: ["lithium"],
    right: ["ibuprofen", "diclofenac", "naproxen", "furosemide", "hydrochlorothiazide", "ramipril", "enalapril", "telmisartan", "losartan"],
    severity: "major",
    label: "Lithium + NSAID/Diuretic/RAASi → lithium toxicity",
    mechanism: "All three reduce renal lithium clearance → toxic levels (> 1.2 mEq/L).",
    management: "Avoid or check lithium level within 5 days of starting/dose-changing. Symptoms of toxicity: tremor, confusion, ataxia, seizures.",
  },
  {
    key: "opioid+cns-depressant",
    left: ["morphine", "oxycodone", "tramadol", "tapentadol", "fentanyl", "buprenorphine", "codeine", "hydromorphone", "pethidine"],
    right: ["alprazolam", "clonazepam", "diazepam", "lorazepam", "midazolam", "nitrazepam", "etizolam", "zolpidem", "zopiclone", "gabapentin", "pregabalin"],
    severity: "critical",
    label: "Opioid + Benzodiazepine / Gabapentinoid → FDA black-box: respiratory depression",
    mechanism: "Additive CNS + respiratory depression via GABAergic + µ-opioid pathways — profound sedation, coma, death (FDA Aug 2016 boxed warning).",
    management: "AVOID this combination unless no alternatives. If essential: lowest effective doses, shortest course, prescribe naloxone rescue kit, warn family. Screen for alcohol/sleep apnoea.",
  },
  {
    key: "simva-lova+cyp3a4",
    left: ["simvastatin", "lovastatin"],
    right: ["cyclosporine", "verapamil", "diltiazem", "itraconazole", "ketoconazole", "clarithromycin", "erythromycin"],
    severity: "critical",
    label: "Simva/Lova + Strong CYP3A4 inhibitor → rhabdomyolysis",
    mechanism: "Simvastatin AUC ↑ 5-20× → dose-dependent myotoxicity, rhabdomyolysis, AKI.",
    management: "CONTRAINDICATED. Switch to Rosuvastatin or Pravastatin (not CYP3A4-metabolised). Simva max 20 mg with amlodipine or verapamil.",
  },

  // ==== Additions from interaction_engine.js port (Feb 2026) ====
  {
    key: "carbapenem+valproate",
    left: ["meropenem", "imipenem", "ertapenem", "doripenem"],
    right: ["valproate", "valproic", "sodium valproate", "divalproex"],
    severity: "critical",
    label: "Carbapenem + Valproate → seizure recurrence",
    mechanism: "Carbapenems drop valproate serum levels by 60-90 % within 24-48 h via glucuronide hydrolysis inhibition and enterohepatic disruption. Loading extra valproate does NOT restore levels.",
    management: "AVOID. Switch antibiotic (piperacillin-tazobactam / cephalosporin) if seizure history. If carbapenem essential, use a bridging AED (levetiracetam / lacosamide) and monitor closely.",
  },
  {
    key: "cipro+theophylline",
    left: ["ciprofloxacin", "norfloxacin"],
    right: ["theophylline", "aminophylline"],
    severity: "major",
    label: "Ciprofloxacin + Theophylline → theophylline toxicity",
    mechanism: "Ciprofloxacin inhibits CYP1A2 → theophylline clearance falls 30-70 % → seizures, arrhythmia, GI symptoms at levels > 20 mcg/mL.",
    management: "Reduce theophylline dose by 50 % on starting cipro or use levofloxacin / moxifloxacin (minimal CYP1A2 effect). Check theophylline level at day 3.",
  },
  {
    key: "doxy-tetra+cation",
    left: ["doxycycline", "tetracycline", "minocycline", "demeclocycline"],
    right: ["calcium carbonate", "ferrous sulphate", "ferrous ascorbate", "iron polymaltose",
            "aluminium hydroxide", "magnesium hydroxide", "sucralfate", "zinc",
            "gelusil", "digene"],
    severity: "moderate",
    label: "Tetracycline / Doxycycline + Iron / Ca / Antacid → absorption ↓ 50-90 %",
    mechanism: "Divalent / trivalent cations chelate tetracyclines in the gut, forming insoluble complexes.",
    management: "Space by ≥ 2-3 h. Preferably take tetracycline 1 h before or 2 h after cations. Warn about hidden calcium in dairy / multivitamins.",
  },
  {
    key: "maoi+serotonergic",
    left: ["phenelzine", "tranylcypromine", "isocarboxazid", "moclobemide", "selegiline", "rasagiline", "linezolid"],
    right: ["sertraline", "fluoxetine", "escitalopram", "paroxetine", "citalopram", "venlafaxine", "duloxetine",
            "mirtazapine", "tramadol", "tapentadol", "pethidine", "meperidine",
            "sumatriptan", "rizatriptan", "zolmitriptan", "eletriptan",
            "dextromethorphan"],
    severity: "critical",
    label: "MAOI + Serotonergic drug → Hypertensive crisis / Serotonin Syndrome",
    mechanism: "MAOIs block serotonin/dopamine/noradrenaline breakdown; combined with any serotonergic agent → sudden BP spikes, hyperthermia, clonus, autonomic instability.",
    management: "ABSOLUTE contraindication. 2-week washout after stopping MAOI (5 weeks for fluoxetine) before any serotonergic drug. Reverse: check BP, cyproheptadine, cooling, ICU.",
  },
  {
    key: "alpha-blocker+pde5",
    left: ["tamsulosin", "prazosin", "terazosin", "doxazosin", "alfuzosin", "silodosin"],
    right: ["sildenafil", "tadalafil", "vardenafil", "avanafil"],
    severity: "major",
    label: "α-blocker + PDE5 inhibitor → symptomatic orthostatic hypotension",
    mechanism: "Both vasodilate — additive drop in BP, especially on standing. Highest risk with non-selective α-blockers (prazosin, doxazosin).",
    management: "Use uro-selective α-blocker (tamsulosin / silodosin) at stable dose ≥ 4 weeks before initiating PDE5. Start PDE5 at lowest dose. Counsel to sit for 30 min after first dose. Avoid combining with alcohol.",
  },
  {
    key: "sacubitril+acei",
    left: ["sacubitril", "sacubitril/valsartan", "entresto"],
    right: ["ramipril", "enalapril", "lisinopril", "perindopril", "captopril", "benazepril", "cilazapril", "fosinopril"],
    severity: "critical",
    label: "Sacubitril/Valsartan + ACE inhibitor → angioedema",
    mechanism: "Both reduce bradykinin breakdown — dual inhibition → tongue/laryngeal angioedema (particularly in Afro-Caribbean patients or on aliskiren).",
    management: "CONTRAINDICATED simultaneously. Require a 36 h washout after the last ACEi dose before starting Entresto. Never restart the ACEi while on Entresto.",
  },
  {
    key: "bisphosphonate+cation",
    left: ["alendronate", "risedronate", "ibandronate", "zoledronate", "pamidronate", "bisphosphonate"],
    right: ["calcium carbonate", "calcium citrate", "ferrous sulphate", "ferrous ascorbate",
            "aluminium hydroxide", "magnesium hydroxide", "gelusil", "digene",
            "multivitamin", "iron polymaltose"],
    severity: "moderate",
    label: "Oral Bisphosphonate + Ca / Iron / Antacid → absorption blocked",
    mechanism: "Chelation in gut — oral bisphosphonate bioavailability is only 1-2 % to begin with, drops to near-zero with any cation.",
    management: "Take bisphosphonate first thing on empty stomach with plain water; wait ≥ 30-60 min upright before ANY food / calcium / iron / coffee. Reinforce weekly-dose ritual.",
  },
  {
    key: "cbz+macrolide",
    left: ["carbamazepin", "oxcarbazepin"],
    right: ["clarithromycin", "erythromycin"],
    severity: "major",
    label: "Carbamazepine + Clarithro / Erythro → CBZ toxicity",
    mechanism: "Strong CYP3A4 inhibition raises CBZ levels 2-3× → ataxia, diplopia, drowsiness, hyponatraemia.",
    management: "Use azithromycin instead (weak CYP3A4 effect). If unavoidable, check CBZ level at day 3 and halve CBZ dose empirically. Watch for SIADH.",
  },
  {
    key: "mtx+booster",
    left: ["methotrexate"],
    right: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "ketorolac", "etoricoxib",
            "trimethoprim", "cotrimoxazole", "sulfamethoxazole",
            "omeprazole", "esomeprazole", "pantoprazole", "rabeprazole", "lansoprazole"],
    severity: "major",
    label: "Methotrexate + NSAID / Trimethoprim / PPI → MTX toxicity",
    mechanism: "NSAIDs and PPIs reduce renal MTX excretion; trimethoprim adds antifolate synergy → myelosuppression, mucositis, hepatotoxicity, esp. with high-dose MTX or CKD.",
    management: "Low-dose weekly MTX (rheum): keep NSAID same brand/dose, add folic acid 5 mg weekly, weekly LFT/CBC. Cotrimoxazole is CONTRAINDICATED with MTX. Space PPIs where possible.",
  },
  {
    key: "octreotide+diabetes",
    left: ["octreotide", "lanreotide", "pasireotide"],
    right: ["insulin", "glargine", "degludec", "detemir", "lispro", "aspart",
            "glimepiride", "gliclazide", "glipizide", "glibenclamide"],
    severity: "moderate",
    label: "Somatostatin analogue + Insulin / SU → dysglycaemia",
    mechanism: "Octreotide blunts both insulin and glucagon release → unpredictable hypo- or hyperglycaemia depending on baseline.",
    management: "Monitor CBG 4×/day for the first week. Reduce basal insulin by 20 % pre-emptively. Educate on hypoglycaemia symptoms; hold SU if CBG < 70.",
  },
  {
    key: "niacin+statin",
    left: ["niacin", "nicotinic acid", "acipimox"],
    right: ["simvastatin", "atorvastatin", "rosuvastatin", "pravastatin", "pitavastatin", "fluvastatin", "lovastatin"],
    severity: "moderate",
    label: "Niacin (high dose) + Statin → myopathy",
    mechanism: "Additive muscle toxicity. Risk highest with niacin ≥ 1 g/day and Simva ≥ 40 mg. Uncertain outcome benefit (AIM-HIGH, HPS2-THRIVE).",
    management: "Prefer maximising statin ± ezetimibe over adding niacin. If niacin used, keep niacin ≤ 1 g/day and statin at moderate intensity. Monitor CK if muscle pain.",
  },
  {
    key: "ondansetron+qt",
    left: ["ondansetron", "granisetron", "palonosetron"],
    right: ["amiodarone", "sotalol", "haloperidol", "quetiapine", "olanzapine",
            "citalopram", "escitalopram", "domperidone",
            "moxifloxacin", "clarithromycin", "erythromycin"],
    severity: "major",
    label: "Ondansetron + QT-prolonging drug → Torsades",
    mechanism: "Additive QT prolongation; ondansetron IV ≥ 16 mg is independently torsadogenic (FDA warning 2011).",
    management: "Use oral ondansetron 4-8 mg PRN, not IV push. Baseline ECG + K/Mg correction if second QT drug on Rx. Consider metoclopramide (avoid > 5 days) or domperidone as alternative antiemetic — but domperidone also prolongs QT.",
  },
  {
    key: "verapamil-diltiazem+colchicine",
    left: ["verapamil", "diltiazem"],
    right: ["colchicine"],
    severity: "major",
    label: "Verapamil / Diltiazem + Colchicine → colchicine toxicity",
    mechanism: "Both inhibit CYP3A4 and P-gp — colchicine AUC rises 2-4×. Life-threatening in CKD, hepatic impairment or elderly.",
    management: "Reduce colchicine to 0.5 mg once every 2-3 days during CCB course. AVOID entirely in CKD stage ≥ 4. Watch for myalgia, GI symptoms, cytopenia.",
  },
  {
    key: "spironolactone+kcl",
    left: ["spironolactone", "eplerenone", "amiloride", "triamterene", "finerenone"],
    right: ["potassium chloride", "kcl", "kcl supplement", "k-supp", "potassium citrate"],
    severity: "major",
    label: "K-sparing diuretic / MRA + Potassium salt → hyperkalaemia",
    mechanism: "Both retain K — additive rise in serum potassium. Highest risk in CKD, diabetes, elderly.",
    management: "Rarely both indicated. Deprescribe the K supplement unless documented deficit. Monitor K weekly for the first month; hold if K > 5.5.",
  },
  {
    key: "ppi+iron-chronic",
    left: ["pantoprazole", "omeprazole", "esomeprazole", "rabeprazole", "lansoprazole"],
    right: ["ferrous sulphate", "ferrous ascorbate", "ferrous fumarate", "iron polymaltose"],
    severity: "minor",
    label: "PPI (chronic) + Oral Iron → reduced iron absorption",
    mechanism: "Gastric acid is needed to reduce Fe³⁺ → Fe²⁺ for absorption. Chronic PPI suppression drops absorption ~30-50 %.",
    management: "Give iron with vitamin C 250-500 mg to maintain absorption. Consider IV iron in refractory anaemia. Reassess PPI necessity beyond 8 weeks.",
  },
  {
    key: "antipsychotic+qt",
    left: ["haloperidol", "quetiapine", "olanzapine", "risperidone", "ziprasidone", "aripiprazole", "clozapine"],
    right: ["amiodarone", "sotalol", "citalopram", "escitalopram", "ondansetron", "domperidone",
            "moxifloxacin", "levofloxacin", "ciprofloxacin", "clarithromycin", "erythromycin",
            "azithromycin"],
    severity: "major",
    label: "Antipsychotic + QT-prolonging drug → Torsades",
    mechanism: "Additive QT prolongation; antipsychotics also cause bradycardia and electrolyte disturbance which lower threshold further.",
    management: "Avoid combination in older adults or those with heart disease. Baseline ECG + K/Mg correction. Prefer aripiprazole among antipsychotics (lowest QT signal); avoid ziprasidone / IV haloperidol with any second QT drug.",
  },

  // ==== Feb-2026 batch 2 — high-yield gaps for Indian diabetes/cardio OPD ====
  {
    key: "doac+enzyme-inducer",
    left: ["apixaban", "rivaroxaban", "dabigatran", "edoxaban"],
    right: ["carbamazepin", "phenytoin", "phenobarbital", "primidone", "rifampin", "rifampicin", "st john's wort"],
    severity: "critical",
    label: "DOAC + Enzyme inducer → treatment failure (stroke / VTE)",
    mechanism: "CYP3A4 + P-gp induction lowers DOAC AUC 30-70 %. Patient looks 'on a blood-thinner' but is effectively sub-therapeutic.",
    management: "AVOID. Switch to warfarin (dose-titrated to INR) or LMWH for the enzyme-inducer course + 2 weeks after. Alternative: change the AED to levetiracetam / lacosamide (non-inducers).",
  },
  {
    key: "fluconazole+sulfonylurea",
    left: ["fluconazole", "zocon", "canesten", "fungicon", "flucos"],
    right: ["glimepiride", "gliclazide", "glipizide", "glibenclamide", "glyburide"],
    severity: "critical",
    label: "Fluconazole + Sulfonylurea → prolonged overnight hypoglycaemia",
    mechanism: "Fluconazole is a strong CYP2C9 inhibitor — SU half-life doubles or triples. Effect lingers 3-5 days after fluconazole stops.",
    management: "Halve SU dose during and 3 days after fluconazole course. Educate patient on hypo symptoms (esp. 2-4 a.m. sweats). Prefer topical antifungal for genital candidiasis in elderly diabetics.",
  },
  {
    key: "isoniazid+phenytoin-cbz",
    left: ["isoniazid", "inh", "isokin", "solonex"],
    right: ["phenytoin", "carbamazepin", "oxcarbazepin"],
    severity: "critical",
    label: "Isoniazid + Phenytoin / Carbamazepine → AED toxicity",
    mechanism: "INH inhibits CYP2C9/2C19 (and 3A4 for CBZ) → phenytoin/CBZ levels rise 2-3×. Toxicity: ataxia, nystagmus, drowsiness, coma. Slow acetylators of INH at highest risk.",
    management: "Check AED level at day 5-7 of INH. Reduce phenytoin by 25-30 % pre-emptively. Consider levetiracetam if newly starting an AED in a TB patient.",
  },
  {
    key: "fentanyl+cyp3a4-inhib",
    left: ["fentanyl", "fentanyl patch", "durogesic"],
    right: ["fluconazole", "clarithromycin", "erythromycin", "ketoconazole", "itraconazole", "voriconazole",
            "ritonavir", "cobicistat", "diltiazem", "verapamil"],
    severity: "critical",
    label: "Fentanyl (transdermal / IV) + Strong CYP3A4 inhibitor → respiratory arrest",
    mechanism: "Fentanyl is 90 % CYP3A4-cleared. Inhibition raises AUC 2-3×; the patch's slow-release reservoir keeps toxic levels for 24-48 h even after stopping.",
    management: "AVOID. Switch to buprenorphine or morphine (both less CYP3A4-dependent) if opioid essential. If unavoidable, halve fentanyl dose, provide naloxone rescue, and monitor SpO₂ closely.",
  },
  {
    key: "nsaid+immunosuppressant",
    left: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "ketorolac", "etoricoxib", "indomethacin"],
    right: ["cyclosporine", "tacrolimus", "everolimus", "sirolimus"],
    severity: "critical",
    label: "NSAID + Calcineurin/mTOR inhibitor → post-transplant AKI",
    mechanism: "Both cause afferent-arteriole vasoconstriction — combined GFR drop 20-40 %, often irreversible if prolonged.",
    management: "AVOID all NSAIDs in transplant recipients. Use paracetamol / short-course tramadol. If NSAID essential for < 3 days, check creatinine + tacro level at 48 h.",
  },
  {
    key: "amiodarone+simva-lova",
    left: ["amiodarone"],
    right: ["simvastatin", "lovastatin"],
    severity: "major",
    label: "Amiodarone + Simvastatin / Lovastatin → myopathy / rhabdomyolysis",
    mechanism: "Amiodarone is a moderate CYP3A4 inhibitor with a 50-day half-life — simvastatin AUC ↑ 2-3× persisting for weeks after amiodarone stop.",
    management: "Simvastatin dose cap = 20 mg with amiodarone (FDA advisory). Preferable to switch to rosuvastatin or pravastatin (not CYP3A4). Check CK if muscle pain.",
  },
  {
    key: "fluconazole+statin",
    left: ["fluconazole", "zocon", "canesten", "fungicon", "flucos"],
    right: ["simvastatin", "atorvastatin", "lovastatin"],
    severity: "major",
    label: "Fluconazole + Simva / Atorva / Lova → myopathy",
    mechanism: "CYP2C9 + moderate CYP3A4 inhibition — statin AUC ↑ 2-4× during a 7-day fluconazole course.",
    management: "Hold statin for the fluconazole course if ≤ 14 days total. Alternatively switch to rosuvastatin / pravastatin. Monitor for muscle pain / dark urine.",
  },
  {
    key: "digoxin+nondhp-ccb",
    left: ["digoxin"],
    right: ["verapamil", "diltiazem"],
    severity: "major",
    label: "Digoxin + Verapamil / Diltiazem → complete AV block + digoxin toxicity",
    mechanism: "Non-DHP CCBs inhibit P-gp → digoxin levels rise 40-80 %. Both also depress AV node → third-degree block risk in elderly.",
    management: "Reduce digoxin dose by 50 % on starting CCB. Check digoxin level at day 5-7. ECG for PR / QRS widening. Avoid combination in patients with sinus node disease.",
  },
  {
    key: "bb+clonidine-withdrawal",
    left: ["bisoprolol", "carvedilol", "metoprolol", "atenolol", "propranolol", "nebivolol", "labetalol"],
    right: ["clonidine", "arkamin", "catapres"],
    severity: "major",
    label: "β-blocker + Clonidine → rebound hypertensive crisis if either stopped abruptly",
    mechanism: "Sudden clonidine withdrawal causes catecholamine surge; unopposed α-stimulation on background β-blockade → severe rebound HTN, sometimes stroke.",
    management: "Never stop clonidine abruptly on a β-blocker. Taper clonidine over 5-7 days FIRST, then discontinue β-blocker. If BOTH need stopping, stop β-blocker first, clonidine last. Counsel patient about refill discipline.",
  },
  {
    key: "warfarin+levothyroxine-abx",
    left: ["warfarin", "acenocoumarol"],
    right: ["levothyroxine", "thyroxine",
            "amoxicillin", "amoxycillin", "amoxiclav", "clavulanate",
            "cefixime", "cefpodoxime", "cefuroxime", "cefdinir", "ceftriaxone"],
    severity: "major",
    label: "Warfarin + Levothyroxine dose change or β-lactam antibiotic → INR shift",
    mechanism: "Levothyroxine raises catabolism of vitamin-K clotting factors → INR rises. β-lactams disrupt gut vitamin-K-producing flora → INR rises. Both effects delayed 3-7 days.",
    management: "Recheck INR 4-7 days after any levothyroxine dose change or β-lactam course. Anticipate 15-25 % warfarin dose reduction. Counsel on bleeding signs.",
  },
  {
    key: "tramadol+ondansetron",
    left: ["tramadol", "ultracet", "tapentadol"],
    right: ["ondansetron", "granisetron", "palonosetron"],
    severity: "major",
    label: "Tramadol + Ondansetron → Serotonin Syndrome + reduced analgesia + QT",
    mechanism: "Both are serotonergic; ondansetron also blocks 5-HT₃ which is the receptor tramadol partly uses for analgesia → poorer pain control. Additive QT prolongation.",
    management: "Prefer metoclopramide or domperidone as antiemetic with tramadol. If ondansetron essential, keep tramadol dose low and monitor for agitation, hyperreflexia, hyperthermia.",
  },
  {
    key: "glp1+narrow-absorption",
    left: ["semaglutide", "liraglutide", "dulaglutide", "tirzepatide", "exenatide"],
    right: ["warfarin", "acenocoumarol", "levothyroxine", "thyroxine",
            "ethinylestradiol", "levonorgestrel", "drospirenone", "oral contraceptive",
            "phenytoin", "carbamazepin", "digoxin"],
    severity: "moderate",
    label: "GLP-1 agonist + drug with narrow absorption window → unpredictable levels",
    mechanism: "GLP-1 agonists delay gastric emptying by 30-50 %. Drugs that need consistent absorption (warfarin, levothyroxine, OCP, phenytoin, digoxin) show more variable trough / INR / TSH after starting or dose-escalating GLP-1.",
    management: "Recheck INR / TSH / phenytoin level 2 weeks after starting or up-titrating GLP-1. Take levothyroxine ≥ 30 min before any food as usual. Counsel OCP users about adding a barrier method during dose escalation (especially oral semaglutide).",
  },
  {
    key: "nsaid+antihypertensive",
    left: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "ketorolac", "etoricoxib", "indomethacin"],
    right: ["ramipril", "enalapril", "lisinopril", "perindopril", "telmisartan", "losartan", "olmesartan", "valsartan", "candesartan", "irbesartan", "sacubitril",
            "bisoprolol", "carvedilol", "metoprolol", "atenolol", "propranolol", "nebivolol",
            "furosemide", "torsemide", "hydrochlorothiazide", "chlorthalidone", "indapamide", "bumetanide"],
    severity: "moderate",
    label: "NSAID + Antihypertensive → BP-lowering effect blunted (~5 mmHg)",
    mechanism: "NSAIDs blunt renal prostaglandin-mediated vasodilation and sodium excretion — dulls the effect of ACEi/ARB, diuretics and β-blockers. Explains many cases of 'resistant hypertension'.",
    management: "Before adding a 4th antihypertensive, check if the patient is on chronic NSAIDs (ask about OTC combos). Prefer topical NSAID / paracetamol. If systemic NSAID essential, monitor home BP; expect to bump antihypertensive doses back once NSAID stops.",
  },
  {
    key: "ivabradine+cyp3a4-inhib",
    left: ["ivabradine", "ivabrad", "ivagen"],
    right: ["fluconazole", "clarithromycin", "erythromycin", "ketoconazole", "itraconazole", "voriconazole",
            "diltiazem", "verapamil",
            "ritonavir", "cobicistat"],
    severity: "major",
    label: "Ivabradine + Strong CYP3A4 inhibitor / non-DHP CCB → profound bradycardia",
    mechanism: "Ivabradine is exclusively CYP3A4-cleared — inhibitors raise AUC 4-10× → HR < 40 bpm, phosphenes, syncope. Non-DHP CCBs add direct SA-node depression.",
    management: "AVOID with strong CYP3A4 inhibitors and non-DHP CCBs (contraindication per label). Prefer amlodipine as CCB if HR control not required. Cardio-selective β-blocker is a safer 2nd rate-control agent in HFrEF.",
  },
  // ==== Gap-fill batch (2026-08) — 19 rules ====
  {
    key: "febuxostat+azathioprine",
    left: ["febuxostat"], right: ["azathioprine", "mercaptopurine", "6-mp"],
    severity: "critical",
    label: "Febuxostat + Azathioprine/6-MP → fatal marrow suppression",
    mechanism: "Febuxostat blocks xanthine oxidase — azathioprine's main detox path — thiopurine levels rise several-fold → pancytopenia. Same mechanism as allopurinol.",
    management: "CONTRAINDICATED. For gout in a thiopurine user, coordinate with the specialist — consider probenecid or manage the thiopurine dose under supervision.",
  },
  {
    key: "cotrimoxazole+mtx",
    left: ["cotrimoxazole", "sulfamethoxazole", "trimethoprim"], right: ["methotrexate"],
    severity: "critical",
    label: "Cotrimoxazole + Methotrexate → pancytopenia",
    mechanism: "Both are antifolates; trimethoprim also cuts renal MTX clearance — additive bone-marrow suppression, worst in elderly/CKD.",
    management: "AVOID. Use an alternative antibiotic (not cotrimoxazole) in any MTX user. If unavoidable, hold MTX that week + CBC at 7 days + folinic acid awareness.",
  },
  {
    key: "cni+cyp3a4",
    left: ["tacrolimus", "ciclosporin", "cyclosporine"],
    right: ["fluconazole", "itraconazole", "ketoconazole", "voriconazole", "clarithromycin", "erythromycin", "diltiazem", "verapamil", "rifampicin", "rifampin"],
    severity: "critical",
    label: "Tacrolimus/Ciclosporin + CYP3A4 modulator → transplant-threatening level swings",
    mechanism: "Calcineurin inhibitors are narrow-margin CYP3A4/P-gp substrates — azoles/macrolides/non-DHP CCBs raise levels 3-5× (nephrotoxicity); rifampicin drops them (rejection).",
    management: "Never add/stop these without the transplant team. If unavoidable, pre-emptive CNI dose change + trough level within 3-5 days.",
  },
  {
    key: "dopamine-blocker+levodopa",
    left: ["metoclopramide", "levosulpiride", "prochlorperazine", "flunarizine"], right: ["levodopa", "carbidopa", "syndopa"],
    severity: "critical",
    label: "Metoclopramide/Levosulpiride + Levodopa → direct antagonism",
    mechanism: "Central D2 blockade cancels levodopa's effect and worsens parkinsonism. Levosulpiride hides inside many Indian PPI combos (e.g. pantoprazole-levosulpiride).",
    management: "AVOID. Use domperidone (peripheral only) for nausea/gastroparesis in Parkinson's. Screen combo brands for hidden levosulpiride.",
  },
  {
    key: "clopidogrel+nsaid",
    left: ["clopidogrel", "prasugrel", "ticagrelor"], right: ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "etoricoxib", "ketorolac"],
    severity: "major",
    label: "Antiplatelet + NSAID → GI bleeding",
    mechanism: "Dual platelet inhibition + NSAID mucosal injury — GI bleed risk rises 2-4×.",
    management: "Prefer paracetamol/topical NSAID. If systemic NSAID essential: shortest course + PPI cover + warn about black stools.",
  },
  {
    key: "ssri+anticoagulant",
    left: ["escitalopram", "sertraline", "fluoxetine", "paroxetine", "citalopram", "venlafaxine", "desvenlafaxine", "duloxetine"],
    right: ["warfarin", "acenocoumarol", "rivaroxaban", "apixaban", "dabigatran"],
    severity: "major",
    label: "SSRI/SNRI + Anticoagulant → bleeding",
    mechanism: "Serotonergic platelet-function impairment adds to anticoagulation; fluoxetine also inhibits warfarin metabolism (CYP2C9).",
    management: "Monitor for bleeding; add PPI if GI risk. With warfarin: extra INR at 1-2 weeks after starting/changing the antidepressant. Mirtazapine is a lower-bleed-risk option.",
  },
  {
    key: "cotrimoxazole+su",
    left: ["cotrimoxazole", "sulfamethoxazole"], right: ["glimepiride", "gliclazide", "glibenclamide", "glipizide"],
    severity: "major",
    label: "Cotrimoxazole + Sulfonylurea → severe hypoglycaemia",
    mechanism: "Sulfamethoxazole inhibits CYP2C9 and displaces SU from albumin — prolonged hypoglycaemia, especially in elderly/CKD.",
    management: "Prefer a different antibiotic. If given: reduce/hold SU during the course, advise sugar checks, and warn about hypo symptoms.",
  },
  {
    key: "macrolide+ccb",
    left: ["clarithromycin", "erythromycin"], right: ["amlodipine", "nifedipine", "cilnidipine", "lercanidipine", "diltiazem", "verapamil"],
    severity: "major",
    label: "Clarithromycin/Erythromycin + CCB → hypotension & AKI",
    mechanism: "CYP3A4 inhibition raises CCB levels — documented excess of shock and acute kidney injury in elderly (JAMA 2013).",
    management: "Use azithromycin instead (weak CYP3A4 effect). If clarithromycin essential, halve the CCB or hold it for the course; check BP standing.",
  },
  {
    key: "dabigatran+pgp",
    left: ["dabigatran"], right: ["verapamil", "amiodarone", "ketoconazole", "dronedarone"],
    severity: "major",
    label: "Dabigatran + P-gp inhibitor → bleeding",
    mechanism: "Dabigatran etexilate is a P-gp substrate — verapamil/amiodarone raise exposure up to ~150%.",
    management: "With verapamil: give dabigatran 2h before, consider 110 mg BD dose tier. Dronedarone/systemic ketoconazole: avoid. Reassess in CKD.",
  },
  {
    key: "fq+cation",
    left: ["ciprofloxacin", "levofloxacin", "ofloxacin", "moxifloxacin", "norfloxacin"],
    right: ["calcium", "shelcal", "iron", "ferrous", "magnesium", "antacid", "gelusil", "digene", "sucralfate", "zinc"],
    severity: "major",
    label: "Fluoroquinolone + Ca/Fe/Mg/antacid → antibiotic failure",
    mechanism: "Divalent/trivalent cations chelate fluoroquinolones in the gut — absorption falls up to 90%; treatment failure and resistance.",
    management: "Give the FQ 2h BEFORE or 6h AFTER any calcium/iron/antacid/sucralfate. Better: pause the supplement for the antibiotic course.",
  },
  {
    key: "theophylline+macrolide",
    left: ["theophylline", "doxophylline", "deriphyllin"], right: ["clarithromycin", "erythromycin"],
    severity: "major",
    label: "Theophylline + Macrolide → theophylline toxicity",
    mechanism: "CYP1A2/3A4 inhibition raises theophylline — narrow index → nausea, tachyarrhythmia, seizures.",
    management: "Use azithromycin instead. If unavoidable: reduce theophylline ~50% and watch for tremor/vomiting/palpitations.",
  },
  {
    key: "phenytoin+fluconazole",
    left: ["phenytoin"], right: ["fluconazole", "voriconazole", "miconazole"],
    severity: "major",
    label: "Phenytoin + Azole → phenytoin toxicity",
    mechanism: "Azoles inhibit CYP2C9 — phenytoin (saturable kinetics) climbs steeply → ataxia, nystagmus, confusion.",
    management: "Prefer topical/alternative antifungal. If systemic azole needed: check phenytoin level at ~1 week; watch for gait unsteadiness.",
  },
  {
    key: "warfarin+tramadol",
    left: ["warfarin", "acenocoumarol"], right: ["tramadol"],
    severity: "moderate",
    label: "Warfarin + Tramadol → INR elevation",
    mechanism: "Tramadol potentiates warfarin's anticoagulant effect (mechanism incompletely defined; serotonergic + CYP contribution).",
    management: "Paracetamol ≤2g/day is the safer analgesic. If tramadol used: INR at 5-7 days, warn about bruising.",
  },
  {
    key: "amlodipine+simvastatin",
    left: ["amlodipine"], right: ["simvastatin", "lovastatin"],
    severity: "moderate",
    label: "Amlodipine + Simvastatin > 20 mg → myopathy",
    mechanism: "Amlodipine is a moderate CYP3A4 inhibitor — doubles simvastatin exposure (FDA caps simvastatin at 20 mg with amlodipine).",
    management: "Cap simvastatin at 20 mg, or simply switch to atorvastatin/rosuvastatin (unaffected).",
  },
  {
    key: "levodopa+iron",
    left: ["levodopa", "syndopa", "carbidopa"], right: ["iron", "ferrous", "orofer"],
    severity: "moderate",
    label: "Levodopa + Iron → reduced levodopa effect",
    mechanism: "Iron chelates levodopa/carbidopa in the gut — absorption falls up to 50%; 'wearing-off' worsens.",
    management: "Separate by ≥2 hours (iron with lunch, levodopa doses away from it). Reassess motor control after starting iron.",
  },
  {
    key: "metformin+topiramate",
    left: ["metformin"], right: ["topiramate", "acetazolamide"],
    severity: "moderate",
    label: "Metformin + Topiramate/Acetazolamide → metabolic acidosis",
    mechanism: "Carbonic-anhydrase inhibitors cause bicarbonate loss — additive with metformin's lactate load, notably in CKD.",
    management: "Usually manageable: confirm eGFR > 60, avoid in CKD, and warn about hyperventilation/unusual fatigue (acidosis signs).",
  },
  {
    key: "thiazide+calcium",
    left: ["hydrochlorothiazide", "chlorthalidone", "indapamide"], right: ["calcium", "shelcal", "calcium carbonate", "cholecalciferol high"],
    severity: "moderate",
    label: "Thiazide + Calcium/Vit D → hypercalcaemia",
    mechanism: "Thiazides cut renal calcium excretion — with calcium + D3 supplements (very common combo) serum calcium can climb → constipation, confusion, stones.",
    management: "Fine at usual doses, but check serum calcium at 3 months, and stop the supplement if polyuria/constipation/confusion appear.",
  },
  {
    key: "digoxin+hcq",
    left: ["digoxin"], right: ["hydroxychloroquine", "chloroquine"],
    severity: "moderate",
    label: "Digoxin + Hydroxychloroquine → digoxin toxicity",
    mechanism: "HCQ inhibits P-gp — digoxin levels roughly double in reported series; both also affect cardiac conduction.",
    management: "Check digoxin level 1-2 weeks after starting HCQ; watch nausea, bradycardia, visual haloes. Consider halving digoxin empirically in elderly.",
  },
  {
    key: "itraconazole+ccb",
    left: ["itraconazole", "ketoconazole"], right: ["amlodipine", "nifedipine", "cilnidipine", "lercanidipine", "felodipine"],
    severity: "moderate",
    label: "Itraconazole + DHP-CCB → oedema/hypotension",
    mechanism: "Strong CYP3A4 inhibition raises CCB levels — marked ankle oedema, flushing, hypotension. Itraconazole is also a negative inotrope.",
    management: "Common with tinea therapy: warn about ankle swelling, consider halving the CCB during the antifungal course; recheck BP at 1 week.",
  },

  // ==== Mined from the 718-drug reference sheet (2026-08) ====
  {
    key: "aminopenicillin+allopurinol",
    left: ["amoxicillin", "amoxycillin", "ampicillin"], right: ["allopurinol"],
    severity: "moderate",
    label: "Amoxicillin/Ampicillin + Allopurinol → higher rash incidence",
    mechanism: "Concurrent allopurinol markedly increases the incidence of aminopenicillin skin rash (classically in hyperuricaemic patients).",
    management: "Warn the patient about rash; a non-urticarial rash here is usually NOT true penicillin allergy — do not mislabel. Consider a cephalosporin/azithromycin if rash history with this combo.",
  },
  {
    key: "cilostazol+cyp-inhib",
    left: ["cilostazol"], right: ["omeprazole", "esomeprazole", "fluconazole", "ketoconazole", "itraconazole", "clarithromycin", "erythromycin", "diltiazem"],
    severity: "moderate",
    label: "Cilostazol + CYP2C19/3A4 inhibitor → raised cilostazol levels",
    mechanism: "Omeprazole (CYP2C19) and azoles/macrolides/diltiazem (CYP3A4) inhibit cilostazol metabolism — headache, palpitations, tachycardia.",
    management: "Halve cilostazol to 50 mg BD while the inhibitor is on board (per label). Prefer pantoprazole/rabeprazole over omeprazole if a PPI is needed. Remember: cilostazol is contraindicated in heart failure of any severity.",
  },
  {
    key: "capecitabine+warfarin",
    left: ["capecitabine", "fluorouracil", "5-fu"], right: ["warfarin", "acenocoumarol"],
    severity: "critical",
    label: "Capecitabine/5-FU + Warfarin → severe bleeding (deaths reported)",
    mechanism: "Fluoropyrimidines inhibit CYP2C9 — INR can spike within days to weeks of starting, and the effect persists up to a month after stopping.",
    management: "AVOID if possible — switch to LMWH/DOAC per oncology advice. If warfarin is unavoidable, check INR at least weekly during and for 1 month after chemotherapy.",
  },
  {
    key: "drospirenone+hyperK",
    left: ["drospirenone"], right: ["ramipril", "enalapril", "lisinopril", "perindopril", "telmisartan", "losartan", "olmesartan", "valsartan", "spironolactone", "eplerenone", "amiloride", "potassium chloride"],
    severity: "moderate",
    label: "Drospirenone OCP + RAAS blocker/K-sparing drug → hyperkalaemia",
    mechanism: "Drospirenone has spironolactone-like anti-mineralocorticoid activity — additive potassium retention with ACEi/ARB/K-sparing diuretics/KCl.",
    management: "Check serum potassium in the first month of the combination, then with routine bloods. Prefer a levonorgestrel-based OCP in CKD or when K+ runs high.",
  },
];

// Return every DDI hit for the current medicine list. Sort output by severity
// (critical → major → moderate → minor).
//   `flags` is an optional {alcoholMisuse, chronicSteroidUse, isImmunosuppressed,
//   nitrateUse} object of patient-context guards from PatientDetail.
export function checkInteractions(medicines, flags = {}) {
  const meds = (medicines || []).filter((m) => m?.brand_name || m?.generic_name || m?.composition);
  if (meds.length < 2 && !Object.values(flags).some(Boolean)) return [];
  const hits = [];
  for (const rule of DDI_RULES) {
    if (rule.guard && !rule.guard(flags, meds)) continue;
    const leftMatch = rxHasAny(meds, rule.left);
    const rightMatch = rxHasAny(meds, rule.right);
    if (!leftMatch || !rightMatch) continue;
    // Self-referential rules (left ∩ right is non-empty, e.g. insulin+insulin
    // when the alcoholMisuse flag is set) intentionally match a single med.
    // Only skip the single-med case for rules whose two sets are disjoint.
    const leftSet = new Set(rule.left || []);
    const overlaps = (rule.right || []).some((r) => leftSet.has(r));
    if (meds.length === 1 && !overlaps) continue;
    hits.push({ ...rule });
  }
  // TRIPLE-drug rules — three groups all present simultaneously.
  for (const t of DDI_TRIPLES) {
    if (t.guard && !t.guard(flags, meds)) continue;
    if (t.groups.every((g) => rxHasAny(meds, g))) hits.push({ ...t, key: t.key });
  }
  // Patient-context-only rules (no medicine pair required, e.g. chronic steroid
  // → osteoporosis reminder, PDE5+nitrate recent-use flag).
  for (const rule of DDI_FLAG_RULES) {
    if (rule.guard && !rule.guard(flags, meds)) continue;
    hits.push({ ...rule });
  }
  const ORDER = { critical: 0, major: 1, moderate: 2, minor: 3 };
  hits.sort((a, b) => (ORDER[a.severity] ?? 9) - (ORDER[b.severity] ?? 9));
  // Dedupe by key.
  const seen = new Set();
  return hits.filter((h) => (seen.has(h.key) ? false : seen.add(h.key)));
}

// ==== TRIPLE-drug rules (3 groups must all be present) ====
export const DDI_TRIPLES = [
  {
    key: "triple-whammy",
    groups: [
      ["ramipril", "enalapril", "lisinopril", "perindopril", "telmisartan", "losartan", "olmesartan", "valsartan", "irbesartan", "candesartan", "sacubitril"],
      ["ibuprofen", "diclofenac", "naproxen", "aceclofenac", "etoricoxib", "indomethacin"],
      ["furosemide", "torsemide", "hydrochlorothiazide", "chlorthalidone", "indapamide", "bumetanide"],
    ],
    severity: "critical",
    label: "Triple Whammy — ACEi/ARB + NSAID + Diuretic → Acute Kidney Injury",
    mechanism: "Three simultaneous hits on renal haemodynamics: NSAID blocks prostaglandin-mediated afferent dilatation, ACEi/ARB blocks efferent constriction, diuretic reduces preload. AKI risk rises 3-4× within days.",
    management: "AVOID this combination — especially in elderly, CKD, or dehydration. Replace NSAID with paracetamol or short-course opioid. If NSAID essential, use lowest dose + close creatinine/K monitoring q3-5 days.",
  },
  {
    key: "triple-hyperK",
    groups: [
      ["spironolactone", "eplerenone", "finerenone", "amiloride", "triamterene"],
      ["ramipril", "enalapril", "lisinopril", "perindopril", "telmisartan", "losartan", "olmesartan", "valsartan", "candesartan", "sacubitril"],
      ["ibuprofen", "diclofenac", "naproxen", "aceclofenac"],
    ],
    severity: "critical",
    label: "Triple Hyperkalaemia Risk — MRA + RAASi + NSAID",
    mechanism: "All three impair potassium excretion by distinct mechanisms → additive/synergistic hyperkalaemia. Life-threatening in CKD, elderly, diabetes.",
    management: "Deprescribe the NSAID (highest risk-benefit ratio to lose). Recheck K within 5-7 days. Consider dietary K restriction. Emergency management if K > 6.0.",
  },
  {
    key: "triple-antithrombotic",
    groups: [
      ["aspirin", "ecosprin"],
      ["clopidogrel", "prasugrel", "ticagrelor"],
      ["warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban", "acenocoumarol"],
    ],
    severity: "critical",
    label: "Triple Antithrombotic Therapy — 2× antiplatelet + oral anticoagulant → severe bleeding",
    mechanism: "Combined DAPT + anticoagulant increases major bleeding risk ~3-4× vs single agent, GI bleed ~5×.",
    management: "Only justifiable short-term post-PCI in AF (max 1-4 weeks per ISTH). Cardiology must approve duration. Add PPI cover. Reassess indication at every visit.",
  },
];

// ==== Patient-context-only rules (no drug pair required) ====
export const DDI_FLAG_RULES = [
  {
    key: "chronic-steroid-bone",
    guard: (flags, meds) => flags?.chronicSteroidUse || rxHasAny(meds || [], ["prednisolone", "prednisone", "dexamethasone", "methylprednisolone", "hydrocortisone", "betamethasone", "deflazacort"]),
    severity: "moderate",
    label: "Chronic corticosteroid → osteoporosis prophylaxis reminder",
    mechanism: "Steroids > 5 mg prednisolone-equivalent for > 3 months reduce bone density 2-5 %/year.",
    management: "Add Calcium 1000-1200 mg/day + Vitamin D 800-1000 IU/day. Consider Bisphosphonate if age ≥ 65, prior fragility fracture, or T-score ≤ -1.5. DEXA baseline + repeat 2-yearly.",
  },
  {
    key: "pde5-nitrate-history",
    guard: (flags, meds) => flags?.nitrateUse && !rxHasAny(meds || [], ["nitroglycerine", "isosorbide", "gtn", "nitrate"]),
    severity: "critical",
    label: "PDE5 inhibitor risk — patient uses nitrates outside this Rx",
    mechanism: "PDE5 inhibitors (sildenafil, tadalafil, vardenafil) are absolutely contraindicated within 24-48 h of any nitrate — nitrate use flagged but not currently on Rx.",
    management: "Verify nitrate exposure timing before prescribing any PDE5 inhibitor. Counsel patient: never take PDE5 + nitrate within 24 h (sildenafil) or 48 h (tadalafil).",
  },
  {
    key: "immunosuppressed-live-vaccine",
    guard: (flags, meds) => flags?.isImmunosuppressed && rxHasAny(meds || [], ["mmr", "varicella", "yellow fever", "rotavirus", "bcg", "live vaccine", "typhoid oral", "nasal flu"]),
    severity: "critical",
    label: "Live vaccine + immunosuppressed patient → CONTRAINDICATED",
    mechanism: "Attenuated live vaccines can replicate uncontrolled in immunosuppressed hosts → disseminated infection.",
    management: "AVOID live vaccine. Use inactivated / recombinant / mRNA alternatives. Defer live vaccine until immunosuppression ends + 3 months.",
  },
  {
    key: "metformin+alcohol-misuse",
    guard: (flags, meds) => !!flags?.alcoholMisuse && rxHasAny(meds || [], ["metformin", "glucophage", "glycomet", "gluformin", "obimet"]),
    severity: "major",
    label: "Metformin + Heavy alcohol → lactic acidosis risk",
    mechanism: "Ethanol shifts hepatic redox state toward lactate production and impairs lactate clearance. Overlap with metformin's own mitochondrial effect can precipitate life-threatening lactic acidosis, particularly in CKD or binge drinking.",
    management: "Counsel: no more than 1-2 standard drinks/day, never on empty stomach, avoid binges. Hold metformin during any illness with vomiting / dehydration. If eGFR < 45 or ongoing misuse, switch to DPP-4 inhibitor or SGLT2i (weigh DKA risk).",
  },
];

const _dummy_end = null;

// Cross-checks the free-text DRUG HISTORY (ongoing/outside medicines) against
// the current Rx using the same DDI rule base. One side of a pair prescribed
// now + the other side mentioned as an ongoing drug → surfaced alert.
export function historyDdiAlerts(medicines, drugHxText) {
  const hx = (drugHxText || "").toLowerCase();
  if (!hx.trim()) return [];
  const blobOf = (m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`.toLowerCase();
  const lvl = (sev) => (sev === "critical" ? "critical" : sev === "major" ? "major" : "caution");
  const out = [];
  for (const rule of DDI_RULES) {
    if (!Array.isArray(rule.left) || !Array.isArray(rule.right)) continue;
    const leftInHx = rule.left.some((t) => hx.includes(t));
    const rightInHx = rule.right.some((t) => hx.includes(t));
    if (!leftInHx && !rightInHx) continue;
    for (const m of medicines || []) {
      const blob = blobOf(m);
      if (!blob.trim()) continue;
      const leftInRx = rule.left.some((t) => blob.includes(t));
      const rightInRx = rule.right.some((t) => blob.includes(t));
      // Skip pairs where both sides are already on the Rx table — the main
      // DDI engine covers those; this is strictly Rx × drug-history.
      if ((leftInRx && rightInHx && !rightInRx) || (rightInRx && leftInHx && !leftInRx)) {
        out.push({
          level: lvl(rule.severity),
          msg: `${rule.label} — the other drug is ONGOING per Drug History. ${rule.management || ""}`,
        });
        break;
      }
    }
  }
  return [...new Map(out.map((a) => [a.msg, a])).values()];
}
